/**
 * Facts about a per-user AVD that are DERIVED or CONSTANT, rather than stored on the registry row.
 *
 * Shared by two consumers: both a colleague's own "Desktop details" expander and the admin
 * inventory's detail sheet print the resource group and the subscription NAME, and neither is a field
 * on `AvdDesktopRecord`. Defined once so the two pages cannot come to disagree about where a
 * desktop lives.
 */

import {
	type AvdDesktopStatus,
	type BundleType,
	type BundleVersionState,
	bundleVersionState,
	GITHUB_WEB_BASE,
	INFRA_REPO,
	INFRA_REPO_OWNER,
} from "@internal/backstage-plugin-avd-common";
import { APP_COMMIT, DESIRED_IAC_VERSIONS } from "@internal/backstage-plugin-deploy-common";

/**
 * The resource group every per-user desktop lives in — a platform constant, the same for everyone,
 * so it is not stored per row. Kept in sync with `infra/bundles/avd` (`resource_group_name`,
 * whose own comment notes that one RG per user-LZ is what makes a fixed name safe).
 */
export const AVD_RESOURCE_GROUP = "rg-backstage-avd";

/**
 * The vended subscription's display name — the name a colleague hands to IT, and the one an operator
 * scans for in the Azure portal. The STORED name when the registry has one: subscriptions are per
 * colleague (ADR-0022), a new colleague's is `backstage-<slug>`, so it cannot be derived. Otherwise
 * the legacy derived `backstage-avd-<slug>` — right for every row without a stored name, because the
 * provision run records one on every `ready`. (docs/app/avd-registry.md.)
 */
export function avdSubscriptionName(slug: string, stored?: string): string {
	return stored || `backstage-avd-${slug}`;
}

/** Desired (this build's stamp) vs actual (the row) IaC version — docs/app/avd-registry.md § Desired vs actual. */
export type DesktopIacVersion = {
	desired?: string;
	actual?: string;
	state: BundleVersionState;
};

/** Desired vs actual for one registry row. A row without a `bundleType` predates it and is an AVD. */
export function desktopIacVersion(
	record: Readonly<{ bundleType?: BundleType; iacVersion?: string; status?: AvdDesktopStatus }>,
): DesktopIacVersion {
	const desired = DESIRED_IAC_VERSIONS[record.bundleType ?? "avd"] || undefined;
	// Teardown keeps the row's last `iacVersion` (upsert preserves omitted fields); no desktop, no version.
	const actual = record.status === "absent" ? undefined : record.iacVersion || undefined;
	return { desired, actual, state: bundleVersionState(desired, actual) };
}

/** How the state reads to a human. */
export function iacVersionStateLabel(state: BundleVersionState): string {
	switch (state) {
		case "current":
			return "Up to date";
		case "behind":
			return "Behind";
		case "unknown":
			return "Unknown";
	}
}

/** The commit the desired version came from (versions are hashes, so no compare URL). Private repo: admins only. */
export function desiredIacCommitUrl(): string | undefined {
	if (!APP_COMMIT) return undefined;
	return `${GITHUB_WEB_BASE}/${INFRA_REPO_OWNER}/${INFRA_REPO}/commit/${APP_COMMIT}`;
}

/** The one-line summary both pages print: `desired · actual · state`, em-dashing a missing side. */
export function iacVersionSummary(version: DesktopIacVersion): string {
	return `desired ${version.desired ?? "—"} · actual ${version.actual ?? "—"} · ${iacVersionStateLabel(
		version.state,
	).toLowerCase()}`;
}
